import { z } from 'zod/v3'

import type {
    ShopContentImage,
    ShopPostEdit,
    ShopPostInput,
} from '@/lib/api/shop-cms-api'
import { m } from '@/lib/paraglide/messages'

// Form soạn bài viết website (khớp server usecase/shop/post_types.go).

export const POST_TITLE_MAX = 300
export const POST_EXCERPT_MAX = 300
export const POST_SLUG_MAX = 120
export const POST_SEO_TITLE_MAX = 70
export const POST_SEO_DESC_MAX = 170
export const POST_GROUP_MAX = 12

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

/** Ảnh đã chọn từ thư viện (id + url để xem trước). */
export interface PickedImage {
    id: string
    url: string
    thumb_url?: string
    alt?: string
}

export interface PostForm {
    title: string
    slug: string
    excerpt: string
    category_id: string
    cover: PickedImage | null
    og: PickedImage | null
    featured: boolean
    content_html: string
    seo_title: string
    seo_description: string
    group_ids: string[]
    /** yyyy-MM-ddTHH:mm (giờ máy) hoặc ''. */
    published_at: string
}

const pick = (
    id: string | null,
    media: Record<string, ShopContentImage>,
): PickedImage | null => {
    if (!id) return null
    const img = media[id]
    return img
        ? { id, url: img.url, thumb_url: img.thumb_url, alt: img.alt }
        : { id, url: '' }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** ISO → giá trị ô datetime-local (giờ máy). */
export function toLocal(iso: string | null): string {
    if (!iso) return ''
    const d = new Date(iso)
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function emptyPostForm(): PostForm {
    return {
        title: '',
        slug: '',
        excerpt: '',
        category_id: '',
        cover: null,
        og: null,
        featured: false,
        content_html: '',
        seo_title: '',
        seo_description: '',
        group_ids: [],
        published_at: '',
    }
}

export function postToForm(p: ShopPostEdit): PostForm {
    const f = p.fields
    return {
        title: f.title,
        slug: f.slug,
        excerpt: f.excerpt ?? '',
        category_id: f.category_id ?? '',
        cover: pick(f.cover_media_id, p.media),
        og: pick(f.og_media_id, p.media),
        featured: f.featured,
        content_html: f.content_html ?? '',
        seo_title: f.seo_title ?? '',
        seo_description: f.seo_description ?? '',
        group_ids: f.group_ids,
        published_at: toLocal(p.published_at),
    }
}

const opt = (s: string) => (s.trim() === '' ? null : s.trim())

export function formToInput(f: PostForm, version?: number): ShopPostInput {
    return {
        ...(version === undefined ? {} : { version }),
        title: f.title.trim(),
        slug: f.slug.trim(),
        excerpt: opt(f.excerpt),
        category_id: f.category_id || null,
        cover_media_id: f.cover?.id ?? null,
        og_media_id: f.og?.id ?? null,
        featured: f.featured,
        content_html: opt(f.content_html),
        seo_title: opt(f.seo_title),
        seo_description: opt(f.seo_description),
        group_ids: f.group_ids,
        published_at: f.published_at
            ? new Date(f.published_at).toISOString()
            : null,
    }
}

const max = (n: number) => m.error_shop_field_too_long({ max: String(n) })

/** Schema form; publish = đòi thêm mô tả ngắn + chuyên mục (như server). */
export const createPostSchema = (publish: boolean) =>
    z
        .object({
            title: z
                .string()
                .trim()
                .min(1, m.cmsPost_error_title_required())
                .max(POST_TITLE_MAX, max(POST_TITLE_MAX)),
            slug: z
                .string()
                .trim()
                .max(POST_SLUG_MAX, max(POST_SLUG_MAX))
                .refine(
                    (s) => s === '' || SLUG_RE.test(s),
                    m.cmsPost_error_slug(),
                ),
            excerpt: z
                .string()
                .trim()
                .max(POST_EXCERPT_MAX, max(POST_EXCERPT_MAX)),
            category_id: z.string(),
            seo_title: z
                .string()
                .trim()
                .max(POST_SEO_TITLE_MAX, max(POST_SEO_TITLE_MAX)),
            seo_description: z
                .string()
                .trim()
                .max(POST_SEO_DESC_MAX, max(POST_SEO_DESC_MAX)),
            group_ids: z.array(z.string()).max(
                POST_GROUP_MAX,
                m.error_shop_field_too_many({
                    max: String(POST_GROUP_MAX),
                }),
            ),
        })
        .passthrough()
        .superRefine((f, ctx) => {
            if (!publish) return
            if (f.excerpt.trim() === '')
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['excerpt'],
                    message: m.cmsPost_error_excerpt_publish(),
                })
            if (!f.category_id)
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['category_id'],
                    message: m.cmsPost_error_category_publish(),
                })
        })

/** Tên ô form của lỗi server (params.field). */
export function postFormField(field: string): string {
    if (field.startsWith('group_ids')) return 'group_ids'
    if (field === 'cover_media_id') return 'cover'
    if (field === 'og_media_id') return 'og'
    return field
}
